import { inject, Injectable, resource, computed } from "@angular/core";
import { IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import { ITaxReturnStatus } from "@app/views-interfaces/tax-return-status-model.interface";
import { AuthService } from "@shared/auth";
import { DashboardsService } from "@shared/dashboards-service";
import type { JobsAndBillingResourceParams } from "@shared/interfaces/jobs-and-billing-common.interface";
import { isJobsBillingDashboard } from "@app/views-interfaces";
import { RoutingParentSyncService } from "@app/routing-utils/routing-parent-sync.service";

export interface IJobsStatusResponse {
    id: string;
    name: {
        "en-CA": string;
        "fr-CA": string;
    };
    active: boolean;
    order: number;
}

export interface IJobsStatusesResponse {
    data: {
        jobStatuses: IJobsStatusResponse[];
    };
}

@Injectable({ providedIn: "root" })
export class JobsAndBillingStatusesService {
    private readonly authService = inject(AuthService, { optional: true });
    private readonly dashboardsService = inject(DashboardsService, { optional: true });
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly routingParentSyncService = inject(RoutingParentSyncService);

    private jobStatusesLoadPromise: Promise<ITaxReturnStatus[]> | null = null;
    private readonly jobStatusesResource = resource<ITaxReturnStatus[], JobsAndBillingResourceParams>({
        defaultValue: [],
        params: () => ({
            currentUser: this.authService?._currentUser() ?? null,
            currentDashboard: this.dashboardsService?.currentDashboard() ?? null,
            currentUrlPath: this.routingParentSyncService.currentUrlPath(),
        }),
        loader: ({ params }: { params: JobsAndBillingResourceParams }) => {
            const { currentUser, currentDashboard, currentUrlPath } = params;
            if (!currentUser || !currentDashboard || !isJobsBillingDashboard(currentDashboard)) return Promise.resolve([]);
            if (currentUrlPath && currentUrlPath !== "/settings/mappingofstatuses" && !currentUrlPath.includes("/card-view")) {
                return Promise.resolve([]);
            }
            return this.getJobStatuses();
        },
    });
    public jobStatuses = computed(() => {
        const statuses = this.jobStatusesResource.value();
        return statuses;
    });

    public jobStatusesMap = computed(() => {
        const jobStatuses = this.jobStatuses();
        const map = new Map<string, ITaxReturnStatus>();
        if (!jobStatuses || !jobStatuses.length) return map;

        jobStatuses.forEach((status) => {
            if (status.Id) map.set(status.Id, status);
        });
        return map;
    });

    public async getJobStatuses(): Promise<ITaxReturnStatus[]> {
        if (!this.jobStatusesLoadPromise) this.jobStatusesLoadPromise = this.loadJobStatuses();
        return this.jobStatusesLoadPromise;
    }

    private async loadJobStatuses(): Promise<ITaxReturnStatus[]> {
        try {
            const result = await this.iframeMessagesService.postMessageResp("getJobsAndBillingStatuses", null, "gotJobsStatuses");
            if (result.status === "success") {
                const jobStatuses = (result.response as IJobsStatusesResponse)?.data?.jobStatuses;
                if (jobStatuses) return jobStatuses.map((status, index) => this.fitResponse(status, index));
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
        return [];
    }

    private fitResponse(response: IJobsStatusResponse, idx: number | string): ITaxReturnStatus {
        return {
            ReturnStatusKey: "created", //why?
            Name: {
                "en-CA": response.name["en-CA"],
                "fr-CA": response.name["fr-CA"],
            },
            TaxAppStatusId: idx.toString(),
            Id: response.id.toString(),
            Active: response.active,
            Order: response.order,
        };
    }
}
